import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Wrench, Save, X, Pencil } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { useToast } from '@/hooks/use-toast';
import type { Equipment, Location } from '@/types/settings';

// Helper function to create default equipment form data - extracted to reduce cognitive complexity (S3776)
function createDefaultEquipmentFormData(): Partial<Equipment> {
  return {
    code: '',
    description: '',
    acquisitionValue: 0,
    acquiredDate: '',
    linkedAssetId: '',
    locationId: '',
    userText1: '',
    userText2: '',
    userText3: '',
    userText4: '',
    userText5: '',
    userText6: '',
    userValue1: 0,
    userValue2: 0,
    userValue3: 0,
    userDate1: '',
    userDate2: '',
    userDate3: '',
    userOption1: '',
    userOption2: '',
    userOption3: '',
    notes: '',
    supplier: {
      code: '',
      name: '',
      telephone: '',
      email: '',
      website: '',
      hirePurchase: false,
      invoiceReference: '',
      purchaseOrderReference: '',
      warrantyStartDate: '',
      warrantyFinishDate: '',
      warrantyCost: 0,
      addresses: [],
    },
  };
}

export default function EquipmentDetailPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const isNew = id === 'new';

  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(isNew);

  // Use extracted helper function to reduce cognitive complexity (S3776)
  const [formData, setFormData] = useState<Partial<Equipment>>(createDefaultEquipmentFormData());

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        
        const locationsData = await dataService.getLocations();
        setLocations(locationsData);

        if (!isNew && id) {
          const equipmentData = await dataService.getEquipmentItem(id);
          setEquipment(equipmentData);
          setFormData(equipmentData);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load equipment. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id, isNew]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isNew) {
        const newEquipment: Equipment = {
          id: String(Date.now()),
          code: formData.code ?? '',
          description: formData.description,
          acquisitionValue: formData.acquisitionValue ?? 0,
          acquiredDate: formData.acquiredDate,
          linkedAssetId: formData.linkedAssetId,
          locationId: formData.locationId,
          userText1: formData.userText1,
          userText2: formData.userText2,
          userText3: formData.userText3,
          userText4: formData.userText4,
          userText5: formData.userText5,
          userText6: formData.userText6,
          userValue1: formData.userValue1,
          userValue2: formData.userValue2,
          userValue3: formData.userValue3,
          userDate1: formData.userDate1,
          userDate2: formData.userDate2,
          userDate3: formData.userDate3,
          userOption1: formData.userOption1,
          userOption2: formData.userOption2,
          userOption3: formData.userOption3,
          notes: formData.notes,
          supplier: formData.supplier,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        
        if (isDemoMode()) {
          toast({
            title: 'Success',
            description: 'Equipment created successfully',
          });
          navigate('/administration/equipment');
        } else {
          await dataService.createEquipment(newEquipment);
          toast({
            title: 'Success',
            description: 'Equipment created successfully',
          });
          navigate('/administration/equipment');
        }
      } else if (equipment) {
        if (isDemoMode()) {
          toast({
            title: 'Success',
            description: 'Equipment updated successfully',
          });
          navigate('/administration/equipment');
        } else {
          await dataService.updateEquipment(equipment.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as Equipment);
          toast({
            title: 'Success',
            description: 'Equipment updated successfully',
          });
          navigate('/administration/equipment');
        }
      }
    } catch (err) {
      console.error('Failed to save equipment:', err);
      toast({
        title: 'Error',
        description: 'Failed to save equipment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading equipment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/administration/equipment')}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Wrench className="h-6 w-6 text-primary" />
                <div>
                  <h1 className="text-2xl font-bold">
                    {isNew ? 'New Equipment' : equipment?.code}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isNew ? 'Create a new equipment record' : 'Equipment details'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditMode && !isNew ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      if (isNew) {
                        navigate('/administration/equipment');
                      } else {
                        setIsEditMode(false);
                        setFormData(equipment!);
                      }
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={!formData.code || saving}>
                    {saving ? (
                      <>
                        <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isNew ? 'Create Equipment' : 'Save Changes'}
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Bar - Enhanced Enterprise Design */}
          <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
            <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
              <TabsTrigger 
                value="details"
                className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
              >
                Details
              </TabsTrigger>
              <TabsTrigger 
                value="userfields"
                className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
              >
                User Fields
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Details Tab */}
            <TabsContent value="details" className="m-0 p-6 space-y-6">
              {/* Equipment Details Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Equipment Details</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Basic equipment information including code, description, and location
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Code *</Label>
                      {isEditMode ? (
                        <Input
                          id="code"
                          value={formData.code}
                          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                          placeholder="e.g., EQ-001"
                        />
                      ) : (
                        <Input
                          id="code"
                          value={formData.code ?? '-'}
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      {isEditMode ? (
                        <Input
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Equipment description"
                        />
                      ) : (
                        <Input
                          id="description"
                          value={formData.description ?? '-'}
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="acquisitionValue">Acquisition Value</Label>
                      {isEditMode ? (
                        <Input
                          id="acquisitionValue"
                          type="number"
                          step="0.01"
                          value={formData.acquisitionValue}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              acquisitionValue: parseFloat(e.target.value) ?? 0,
                            })
                          }
                          placeholder="0.00"
                        />
                      ) : (
                        <Input
                          id="acquisitionValue"
                          value={formatCurrency(formData.acquisitionValue ?? 0)}
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="acquiredDate">Acquired Date</Label>
                      {isEditMode ? (
                        <Input
                          id="acquiredDate"
                          type="date"
                          value={formData.acquiredDate}
                          onChange={(e) => setFormData({ ...formData, acquiredDate: e.target.value })}
                        />
                      ) : (
                        <Input
                          id="acquiredDate"
                          value={formData.acquiredDate ?? '-'}
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="linkedAsset">Linked Asset</Label>
                      {isEditMode ? (
                        <Input
                          id="linkedAsset"
                          value={formData.linkedAssetId}
                          onChange={(e) => setFormData({ ...formData, linkedAssetId: e.target.value })}
                          placeholder="Asset ID"
                        />
                      ) : (
                        <Input
                          id="linkedAsset"
                          value={formData.linkedAssetId ?? '-'}
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      {isEditMode ? (
                        <Select
                          value={formData.locationId}
                          onValueChange={(value: string) => setFormData({ ...formData, locationId: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem key={location.id} value={location.id}>
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="location"
                          value={
                            locations.find((l) => l.id === formData.locationId)?.name ?? '-'
                          }
                          readOnly
                          className="bg-muted"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Supplier Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Supplier Information</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Supplier contact details and purchase information
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="supplierCode">Supplier Code</Label>
                      <Input
                        id="supplierCode"
                        value={formData.supplier?.code}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, code: e.target.value },
                          })
                        }
                        placeholder="SUP-001"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="supplierName">Supplier Name</Label>
                      <Input
                        id="supplierName"
                        value={formData.supplier?.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, name: e.target.value },
                          })
                        }
                        placeholder="Supplier name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telephone">Telephone</Label>
                      <Input
                        id="telephone"
                        value={formData.supplier?.telephone}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, telephone: e.target.value },
                          })
                        }
                        placeholder="+1-555-0123"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.supplier?.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, email: e.target.value },
                          })
                        }
                        placeholder="supplier@example.com"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2 lg:col-span-3">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        value={formData.supplier?.website}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, website: e.target.value },
                          })
                        }
                        placeholder="https://www.supplier.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invoiceRef">Invoice Reference</Label>
                      <Input
                        id="invoiceRef"
                        value={formData.supplier?.invoiceReference}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: {
                              ...formData.supplier!,
                              invoiceReference: e.target.value,
                            },
                          })
                        }
                        placeholder="INV-2024-001"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="poRef">Purchase Order Reference</Label>
                      <Input
                        id="poRef"
                        value={formData.supplier?.purchaseOrderReference}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: {
                              ...formData.supplier!,
                              purchaseOrderReference: e.target.value,
                            },
                          })
                        }
                        placeholder="PO-2024-001"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="hirePurchase"
                        checked={formData.supplier?.hirePurchase}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            supplier: { ...formData.supplier!, hirePurchase: checked },
                          })
                        }
                      />
                      <Label htmlFor="hirePurchase" className="cursor-pointer">
                        Hire Purchase
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Warranty Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Warranty Information</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Warranty details and coverage information
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="warrantyStart">Warranty Start Date</Label>
                      <Input
                        id="warrantyStart"
                        type="date"
                        value={formData.supplier?.warrantyStartDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: {
                              ...formData.supplier!,
                              warrantyStartDate: e.target.value,
                            },
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="warrantyFinish">Warranty Finish Date</Label>
                      <Input
                        id="warrantyFinish"
                        type="date"
                        value={formData.supplier?.warrantyFinishDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: {
                              ...formData.supplier!,
                              warrantyFinishDate: e.target.value,
                            },
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="warrantyCost">Warranty Cost</Label>
                      <Input
                        id="warrantyCost"
                        type="number"
                        step="0.01"
                        value={formData.supplier?.warrantyCost}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            supplier: {
                              ...formData.supplier!,
                              warrantyCost: parseFloat(e.target.value) ?? 0,
                            },
                          })
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Additional notes and comments about this equipment
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    {isEditMode ? (
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Additional notes about this equipment"
                        rows={10}
                      />
                    ) : (
                      <Textarea
                        id="notes"
                        value={formData.notes ?? 'No notes'}
                        readOnly
                        className="bg-muted"
                        rows={10}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Fields Tab */}
            <TabsContent value="userfields" className="m-0 p-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>User Fields</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Custom fields for additional equipment information and tracking
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Text Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3, 4, 5, 6].map((num) => (
                        <div key={num} className="space-y-2">
                          <Label htmlFor={`userText${num}`}>User Text {num}</Label>
                          <Input
                            id={`userText${num}`}
                            value={formData[`userText${num}` as keyof Equipment] as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                              setFormData({
                                ...formData,
                                [`userText${num}`]: e.target.value,
                              })
                            }
                            placeholder={`Text field ${num}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Value Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="space-y-2">
                          <Label htmlFor={`userValue${num}`}>User Value {num}</Label>
                          <Input
                            id={`userValue${num}`}
                            type="number"
                            value={formData[`userValue${num}` as keyof Equipment] as number}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                              setFormData({
                                ...formData,
                                [`userValue${num}`]: parseFloat(e.target.value) ?? 0,
                              })
                            }
                            placeholder={`Value ${num}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Date Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="space-y-2">
                          <Label htmlFor={`userDate${num}`}>User Date {num}</Label>
                          <Input
                            id={`userDate${num}`}
                            type="date"
                            value={formData[`userDate${num}` as keyof Equipment] as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                              setFormData({
                                ...formData,
                                [`userDate${num}`]: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Option Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="space-y-2">
                          <Label htmlFor={`userOption${num}`}>User Option {num}</Label>
                          <Input
                            id={`userOption${num}`}
                            value={formData[`userOption${num}` as keyof Equipment] as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                              setFormData({
                                ...formData,
                                [`userOption${num}`]: e.target.value,
                              })
                            }
                            placeholder={`Option ${num}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
